import React, {memo, useMemo} from 'react';
import {FormikTextFieldProps} from './FormikTextField';
import PassphraseField from 'components/inputs/PassphraseField';

import {IconButton, InputAdornment} from '@mui/material';
import EditIcon from '@mui/icons-material/CreateOutlined';
import {isObfuscated} from 'common/stringUtils';
import {readOnlyContext} from 'common/context/ReadOnlyContext';
import {useController, useFormContext} from 'react-hook-form';
import {SetValueOptions} from 'components/hook-form/helpers';
import {useRegisterLabel} from 'common/context/FormContext';
import {useToggle} from 'common/hooks/useToggle';
import {useEnsureFieldValue} from 'common/hooks/useEnsureFieldValue';
import {passwordContext} from 'common/context/PasswordContext';
import {useSelector} from 'react-redux';
import {loginSelectors} from 'features/login/redux';
import {FetchPassButton} from './FetchPassButton';

type Props = FormikTextFieldProps & {
   hideButton?: boolean;
   fetchUrl?: string;
};

type PassphraseProps = Props & {
   value: string;
   setValue: (val: string) => void;
   onChange: FormikTextFieldProps['onChange'];
   onBlur: FormikTextFieldProps['onBlur'];
};

const Passphrase = memo(
   ({
      name,
      value,
      onBlur: _onBlur,
      onChange,
      setValue,
      fetchUrl,
      ...restProps
   }: PassphraseProps) => {
      const ref = React.useRef<HTMLInputElement>();

      const [isEdit, setEdit] = React.useState(!value);
      const readOnly = React.useContext(readOnlyContext);
      const _fetchUrl = React.useContext(passwordContext);
      const isShowPasswordPermit = useSelector(loginSelectors.isShowPasswordPermit);

      const obfuscated = useMemo(() => isObfuscated(value), [value]);

      React.useEffect(() => {
         if (obfuscated) {
            setEdit(false);
         }
      }, [setEdit, obfuscated]);

      const edit = React.useCallback(() => {
         obfuscated && setValue('');
         setEdit(true);
         setTimeout(() => ref.current?.focus?.());
      }, [setValue, setEdit, obfuscated]);

      const onBlur = React.useCallback(
         (ev: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            // console.debug(ev);
            setEdit(false);
            _onBlur?.(ev);
         },
         [_onBlur]
      );

      const disabled = readOnly || restProps.disabled;

      const isPermit = (fetchUrl || _fetchUrl) && isShowPasswordPermit;

      const [visible, toggleVisibility, setVisible] = useToggle();

      return (
         <PassphraseField
            name={name}
            value={value}
            onChange={onChange}
            {...restProps}
            type={visible ? 'text' : 'password'}
            onBlur={onBlur}
            inputRef={ref}
            disabled={disabled || !isEdit}
            InputProps={{
               endAdornment: (
                  <>
                     <InputAdornment position="end">
                        {value && isPermit && (
                           <FetchPassButton
                              url={fetchUrl || _fetchUrl}
                              obfuscated={obfuscated}
                              setValue={setValue}
                              setVisible={setVisible}
                              toggleVisibility={toggleVisibility}
                           />
                        )}
                        {!disabled ? (
                           <IconButton aria-label="edit" size={'small'} onClick={edit} edge="end">
                              {isEdit ? null : <EditIcon />}
                           </IconButton>
                        ) : undefined}
                     </InputAdornment>
                  </>
               ),
            }}
         />
      );
   }
);

const HookFormPassphrase = (props: Props) => {
   const {
      field: {onChange, onBlur, value},
      fieldState: {error: _error},
   } = useController({
      name: props.name,
      // defaultValue: '',
   });
   useRegisterLabel(props.name, props.placeholder || (props.label as string));
   const {setValue} = useFormContext();
   const resetField = useEnsureFieldValue();
   const setV = React.useCallback(
      (val: string, reset = false) =>
         reset ? resetField(props.name, val) : setValue(props.name, val, SetValueOptions),
      [setValue, props.name]
   );

   const error = _error?.message;
   return (
      <Passphrase
         {...props}
         value={value}
         onChange={onChange}
         onBlur={onBlur}
         setValue={setV}
         error={!!error}
         helperText={error}
      />
   );
};

export default HookFormPassphrase;
